import { Input } from '@mui/material';
import Box from '@mui/material/Box';
import MuiSlider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { ChangeEvent, FC, ReactNode, useEffect, useState } from 'react';

interface SliderProps {
    label: string | ReactNode;
    min: number;
    max: number;
    value: number;
    step?: number;
    onChange: (value: number) => void;
    disabled?: boolean;
    showDecimals?: boolean;
}

export const Slider: FC<SliderProps> = ({
    label,
    min,
    max,
    value,
    step = 1,
    onChange,
    disabled = false,
    showDecimals = true,
}) => {
    const formatValue = (val: number) =>
        showDecimals ? val.toFixed(2) : val.toString();

    const [inputValue, setInputValue] = useState(formatValue(value));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setInputValue(formatValue(value));
    }, [value, showDecimals]);

    const handleSliderChange = (_: Event, newValue: number | number[]) => {
        if (disabled) return;
        setError(null);
        const validValue = validateValue(newValue as number);
        setInputValue(formatValue(validValue));
        onChange(validValue);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;
        const rawValue = event.target.value;
        const normalizedValue = rawValue.replace(',', '.');
        setInputValue(normalizedValue);

        const numericValue = parseFloat(normalizedValue);
        if (!isNaN(numericValue)) {
            const validValue = validateValue(numericValue);
            if (validValue === numericValue) {
                setError(null);
                onChange(validValue);
            } else {
                setError(`Value must be between ${min} and ${max}`);
            }
        }
    };

    const validateValue = (value: number) => {
        return Math.min(Math.max(value, min), max);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
            }}
        >
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={14}>{label}</Typography>
                <Input
                    sx={{
                        width: 50,
                        textAlign: 'right',
                        fontSize: 12,
                        p: 0,
                        border: 'none',
                        '&.MuiInput-root::before': {
                            borderBottom: 'none',
                        },
                        'input::-webkit-inner-spin-button': {
                            '-webkit-appearance': 'none',
                            margin: 0,
                        },
                    }}
                    inputProps={{
                        step: step,
                        min: min,
                        max: max,
                        type: 'text',
                        'aria-label': 'slider value input',
                        style: { border: 'none', textAlign: 'right' },
                    }}
                    value={inputValue}
                    onChange={handleInputChange}
                    error={Boolean(error)}
                    disabled={disabled}
                />
            </Box>
            {error && (
                <Typography color="error" fontSize={12}>
                    {error}
                </Typography>
            )}
            <MuiSlider
                value={parseFloat(inputValue)}
                onChange={handleSliderChange}
                aria-labelledby="custom-slider-component"
                data-testid="custom-slider-component"
                step={step}
                min={min}
                max={max}
                disabled={disabled}
                sx={(theme) => ({
                    '& .MuiSlider-thumb': {
                        border: disabled
                            ? `2px solid ${theme.palette.text.secondary}`
                            : '2px solid primary.main',
                    },
                })}
            />
        </Box>
    );
};
